import { isNative } from '../native';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { c, fmtDate } from '../theme';
import { Notice, Wordmark } from '../components/Brand';
import { Menu, surveyUrl } from '../components/Menu';
import { VioletCTA } from '../components/AuthScene';
import { OfferSummary } from '../components/OfferPanel';
import { ModuleGroups } from '../components/ModuleGroups';
import { LoopingVideo } from '../components/LoopingVideo';
import { BRAND_GRADIENT } from '../components/AuthScene';

/** The sign-in screens' light scrim: violet glow over warm frost, so ink text reads over the clip. */
const CARD_SCRIM =
  'radial-gradient(70% 70% at 85% 10%, rgba(106,43,217,0.35) 0%, rgba(106,43,217,0) 70%), radial-gradient(60% 60% at 10% 90%, rgba(106,43,217,0.22) 0%, rgba(106,43,217,0) 70%), linear-gradient(180deg, rgba(245,238,219,0.55) 0%, rgba(245,238,219,0.62) 50%, rgba(245,238,219,0.78) 100%)';
import { BottomLineRing, type RingSegment } from '../components/ScoreRing';

/** The bottom line's weights, for a measure whose row no longer states one (it was not scored). */
const NOMINAL_WEIGHT: Record<string, number> = { Price: 30, 'Property & risk': 20, 'Your fit': 30, 'Location & outlook': 20 };
import { api, type ReportState, type ReportSummary, type User } from '../api';

export function Report({ user, reports }: { user: User | null; reports: ReportSummary[] }) {
  const { id = '' } = useParams();
  const [state, setState] = useState<ReportState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState(false);
  const navigate = useNavigate();
  const restore = !!(useLocation().state as { restore?: boolean } | null)?.restore;

  // Coming back from a module with its back arrow: return to the same place on the overview.
  useEffect(() => {
    if (!state?.dossier) return;
    let y = 0;
    try {
      y = restore ? Number(sessionStorage.getItem(`rb-ov-y-${id}`) ?? 0) : 0;
    } catch {
      /* storage blocked: start at the top */
    }
    if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [state, restore, id]);

  useEffect(() => {
    let live = true;
    api
      .report(id)
      .then((r) => {
        if (!live) return;
        if (r.status === 'assembling') navigate(`/assembling/${id}`, { replace: true });
        else setState(r);
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : 'Could not load the report.'));
    return () => {
      live = false;
    };
  }, [id, navigate]);

  if (error) {
    return (
      <Stage>
        <div style={{ padding: '48px var(--gx)' }}>
          <Notice tone="error">{error}</Notice>
        </div>
      </Stage>
    );
  }

  if (!state?.dossier) {
    return (
      <Stage>
        <div style={{ padding: '48px var(--gx)', fontSize: 12, letterSpacing: '0.16em', color: c.ink5, fontWeight: 600 }}>
          LOADING…
        </div>
      </Stage>
    );
  }

  const d = state.dossier;
  const assembling = reports.find((r) => r.status === 'assembling') ?? null;
  const position = d.modules.find((m) => m.key === 'your-position') ?? null;
  // The measures behind the bottom line, one ring segment each, read from the
  // module's own rows so the ring and the module cannot disagree.
  const segments: RingSegment[] = (position?.rows ?? []).map((r) => {
    const scored = /^\d+(\.\d)?$/.test(r.v);
    return {
      label: r.k,
      weight: NOMINAL_WEIGHT[r.k] ?? 25,
      result: scored ? r.v : 'Not scored',
      unit: scored ? '/10' : '',
      note: (r.s.charAt(0).toUpperCase() + r.s.slice(1)).replace(/ — /g, ', '),
    };
  });
  // The Bottom Line out of 10, coloured by range (red under 4, amber to 6.9, green from 7).
  const score10 = position && /^\d+(\.\d)?$/.test(position.hero.value) ? Number(position.hero.value) : null;
  const scoreColour = score10 == null ? undefined : score10 >= 7 ? '#1E8A5A' : score10 >= 4 ? '#C9741A' : c.red;
  // What the listing agency's guide may fetch, on its quoting record (Agent Intel).
  const agentRow = d.modules.find((m) => m.key === 'agent-intel')?.rows.find((r) => r.k === 'What this guide may fetch');
  const agentPrice = agentRow && /^\$[\d,]+$/.test(agentRow.v) ? Number(agentRow.v.replace(/[$,]/g, '')) : null;
  // "5 bed · 2 bath · 6 car · house — for sale 1 Oct 2026 · 3 days on market"
  const [facts, listing] = d.attributes.split(' — ');
  const attrs = { facts, listing: listing ? listing.charAt(0).toUpperCase() + listing.slice(1) : '' };

  return (
    <Stage>
      {/* 1–3 — The property and its bottom line, on one screen (Property Score handoff) */}
      <div style={{ position: 'relative', flex: 'none', display: 'flex', flexDirection: 'column', paddingBottom: 34 }}>
        <div style={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '48px var(--gx) 0' }}>
          <button
            type="button"
            className="rb-back"
            aria-label="Back to my reports"
            onClick={() => navigate('/portfolio')}
            style={{ width: 36, height: 36, margin: '-6px 0 -6px -8px', borderRadius: '50%', border: `1px solid ${c.ink18}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.ink }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M8.5 3L4 7l4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <Wordmark />
          <Menu
            current="report"
            user={user}
            reportCount={reports.length}
            currentReport={{ id, address: d.address.full }}
            assembling={assembling ? { id: assembling.id, address: assembling.address } : null}
          />
        </div>

        {/* The property, in its own card above the bottom line */}
        <div
          style={{
            position: 'relative',
            zIndex: 3,
            margin: '28px 16px 0',
            padding: '24px 24px 20px',
            borderRadius: 28,
            overflow: 'hidden',
            // Video and blur layers ignore a parent's rounded clip in Chrome
            // unless the card is its own layer.
            isolation: 'isolate',
            transform: 'translateZ(0)',
            WebkitMaskImage: '-webkit-radial-gradient(white, black)',
            background: c.section1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {/* The onboarding clip's water, moving behind a frosted light scrim (comment #54) */}
          {/* Zoomed into the clip's top-left corner, which stays open water for the
              whole loop; the people come in from the right and below. */}
          <div aria-hidden style={{ position: 'absolute', inset: 0, transform: 'scale(2.25)', transformOrigin: 'top left' }}>
            <LoopingVideo src="/onboarding-bg.mp4" fallback={BRAND_GRADIENT} objectPosition="left top" />
          </div>
          <div aria-hidden style={{ position: 'absolute', inset: 0, background: CARD_SCRIM, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }} />
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.red }} />
            <span style={{ fontSize: 9, letterSpacing: '0.2em', fontWeight: 600, color: c.ink55 }}>{fmtDate(d.generatedAt)} · PROPERTY REPORT</span>
          </div>
          <div style={{ fontSize: d.address.line1.length > 18 ? 26 : 32, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.03em', color: c.violet, paddingTop: 14 }}>
            {d.address.line1}
          </div>
          <div style={{ fontSize: 10, letterSpacing: '0.2em', fontWeight: 700, color: c.ink, padding: '10px 0 16px' }}>
            {`${d.address.locality} ${d.address.state} ${d.address.postcode}`.trim().toUpperCase()}
            <span style={{ color: c.ink6, letterSpacing: '0.16em' }}>{`  ·  ${d.landSqm != null ? `${Math.round(d.landSqm)} M²` : 'LAND N/A'}`}</span>
          </div>
          <div style={{ alignSelf: 'stretch', height: 1, background: c.hairline }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: c.ink }}>{attrs.facts}</div>
            {attrs.listing && <div style={{ fontSize: 11, color: c.ink6 }}>{attrs.listing}</div>}
          </div>
          </div>
        </div>

        <div style={{ paddingTop: 40 }}>
          <BottomLineRing
            score={position?.hero.value ?? '—'}
            unit={position?.hero.unit ?? ''}
            label={`READYBUY ${position?.hero.label ?? 'BOTTOM LINE'}`}
            summary={position?.hero.caption}
            scoreColour={scoreColour}
            segments={segments}
            onPick={setPicked}
          />
        </div>

        {/* The ring's one instruction, right under it */}
        {segments.length > 0 && (
          <div style={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'center', pointerEvents: 'none', marginTop: -6 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '9px 16px 9px 12px',
                borderRadius: 999,
                background: 'rgba(106,43,217,0.1)',
                border: '1px solid rgba(106,43,217,0.35)',
                color: c.violet,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.violet, animation: 'throb 2.4s ease-in-out infinite' }} />
              {picked ? 'Tap the centre to see the overall' : 'Tap a segment to see its figure'}
            </div>
          </div>
        )}

      </div>

      {/* 4 — The offer window at a glance, then the way into the full tab */}
      {d.offer && (
        <Section shade={c.section1}>
          <OfferSummary offer={d.offer} agentPrice={agentPrice} />
        </Section>
      )}

      {/* 7 — Modules panel: tinted cards straight on the screen grey */}
      <Section shade={c.section2}>
      <div
        style={{
          padding: '44px 0 36px',
          display: 'flex',
          flexDirection: 'column',
          gap: 36,
        }}
      >
        <ModuleGroups
          reportId={id}
          restore={restore}
          modules={d.modules}
          onOpen={(key) => {
            try {
              sessionStorage.setItem(`rb-ov-y-${id}`, String(window.scrollY));
            } catch {
              /* storage blocked */
            }
            navigate(`/report/${id}/${key}`);
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '4px var(--gx) 0' }}>
          <button
            onClick={() => navigate(`/report/${id}/sources`)}
            aria-label="Sources and reliability"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: `1px solid ${c.ink25}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              color: c.ink,
            }}
          >
            ⚑
          </button>
        </div>
      </div>

      </Section>

      {/* The tester survey, outside the app (web beta only) */}
      {!isNative && <Section shade={c.section1}>
        <div style={{ padding: '26px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.2em', fontWeight: 600, color: c.red }}>YOU ARE TESTING READYBUY</div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            How did this report go<span style={{ color: c.red }}>?</span>
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: c.ink6 }}>A short survey, about 5 minutes. It opens in a new tab, so your report stays here.</div>
          <VioletCTA onClick={() => window.open(surveyUrl(user), '_blank', 'noopener')}>give feedback</VioletCTA>
        </div>
      </Section>}
    </Stage>
  );
}

/** One overview section below the hero: its own grey, rounded, inset from the frame. */
function Section({ shade, last, children }: { shade: string; last?: boolean; children: React.ReactNode }) {
  return (
    <div
      style={{
        margin: last ? '0 8px 8px' : '0 8px 8px',
        borderRadius: 32,
        overflow: 'hidden',
        background: shade,
        ['--rb-surface' as string]: shade,
      }}
    >
      {children}
    </div>
  );
}

/** The report scrolls, so its column has no fixed height. */
function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rb-stage"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: c.page,
        padding: '40px 0 60px',
      }}
    >
      <div
        className="rb-shell"
        style={{
          width: 390,
          maxWidth: '100%',
          background: c.screen,
          color: c.ink,
          borderRadius: '40px 40px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}
